import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/// <reference path="../recharts.d.ts" />
import React, { useState, useEffect, useMemo, useCallback, useRef, } from "react";
import { Table, Button, Space, Popover, Checkbox, Divider, Tag, Switch, Badge, Select, Progress, } from "antd";
import { SettingOutlined, EditOutlined, DeleteOutlined, SettingOutlined as TestIcon, } from "@ant-design/icons";
import { Resizable } from "react-resizable";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Legend, ReferenceLine, } from "recharts";
import "react-resizable/css/styles.css";
// 在组件顶部添加配置常量
const MIN_COLUMN_WIDTH = 10; // 最小列宽（设置为很小的值，允许列宽缩到很小）
const MAX_COLUMN_WIDTH = 600; // 最大列宽
const MiniChart = ({ data, chartType, xField, height = 40, width, color = "#1890ff", smooth = false, referenceLines, }) => {
    // 如果没有数据，返回空
    if (!data || data.length === 0) {
        return _jsx("div", { style: { height, width: width || "100%" } });
    }
    // 处理数据格式
    let processedData = [];
    let yFields = [];
    let seriesField;
    // 检查数据格式
    const firstItem = data[0];
    if (typeof firstItem === "number") {
        // 如果数据是数字数组，转换为对象格式
        processedData = data.map((item, index) => ({
            [xField || "index"]: index,
            value: typeof item === 'number' ? item : 0,
        }));
        yFields = ["value"];
    }
    else if (Array.isArray(firstItem)) {
        // 如果数据是二维数组，转换为多系列格式（长格式）
        const maxLength = Math.max(...data.map((arr) => (Array.isArray(arr) ? arr.length : 0)), 0);
        processedData = [];
        for (let i = 0; i < maxLength; i++) {
            data.forEach((arr, seriesIndex) => {
                if (Array.isArray(arr)) {
                    processedData.push({
                        [xField || "index"]: i,
                        type: `series${seriesIndex}`,
                        value: arr[i] ?? 0,
                    });
                }
            });
        }
        seriesField = "type";
        yFields = ["value"];
    }
    else if (typeof firstItem === "object") {
        // 如果数据已经是对象格式
        processedData = data;
        // 尝试自动检测 yField
        const keys = Object.keys(firstItem);
        const xKey = xField || "index";
        yFields = keys.filter((key) => key !== xKey && typeof firstItem[key] === "number");
        // 如果有多个数值字段，使用多系列
        if (yFields.length > 1) {
            seriesField = "type";
            // 转换为长格式数据
            const longData = [];
            processedData.forEach((item) => {
                yFields.forEach((field) => {
                    longData.push({
                        [xKey]: item[xKey] ?? item[keys[0]],
                        type: field,
                        value: item[field],
                    });
                });
            });
            processedData = longData;
            yFields = ["value"];
        }
        else {
            yFields = yFields.length > 0 ? [yFields[0]] : ["value"];
        }
    }
    // 如果没有找到有效的数据字段，使用默认值
    if (yFields.length === 0) {
        yFields = ["value"];
    }
    // 确保最小高度为16px，但使用实际高度进行渲染
    const chartHeight = Math.max(height, 16);
    const chartWidth = width || "100%";
    // 处理颜色
    const colors = Array.isArray(color) ? color : [color];
    const primaryColor = colors[0] || "#1890ff";
    // 折线图配置
    if (chartType === "line") {
        // 如果是多系列数据
        if (seriesField) {
            // 按系列分组数据
            const seriesMap = new Map();
            processedData.forEach((item) => {
                const seriesType = item[seriesField];
                if (!seriesMap.has(seriesType)) {
                    seriesMap.set(seriesType, []);
                }
                seriesMap.get(seriesType).push(item);
            });
            // 转换为 recharts 需要的格式（按 x 值分组）
            const xValueMap = new Map();
            processedData.forEach((item) => {
                const xValue = item[xField || "index"];
                if (!xValueMap.has(xValue)) {
                    xValueMap.set(xValue, { [xField || "index"]: xValue });
                }
                const seriesType = item[seriesField];
                xValueMap.get(xValue)[seriesType] = item[yFields[0] || "value"];
            });
            const chartData = Array.from(xValueMap.values());
            return (_jsx("div", { style: { height: chartHeight, width: chartWidth }, children: _jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(LineChart, { data: chartData, margin: { top: 0, right: 0, bottom: 0, left: 0 }, children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: () => null }), _jsx(Legend, { content: () => null }), Array.from(seriesMap.keys()).map((seriesType, index) => (_jsx(Line, { type: smooth ? "monotone" : "linear", dataKey: seriesType, stroke: colors[index % colors.length], strokeWidth: chartHeight <= 16 ? 1.5 : 2, dot: false, isAnimationActive: false }, seriesType))), referenceLines?.map((refLine, index) => (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, `ref-line-${refLine.value}-${index}`)))] }) }) }));
        }
        // 单系列折线图
        return (_jsx("div", { style: { height: chartHeight, width: chartWidth }, children: _jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(LineChart, { data: processedData, margin: { top: 0, right: 0, bottom: 0, left: 0 }, children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: () => null }), _jsx(Line, { type: smooth ? "monotone" : "linear", dataKey: yFields[0] || "value", stroke: primaryColor, strokeWidth: chartHeight <= 16 ? 1.5 : 2, dot: false, isAnimationActive: false }), referenceLines?.map((refLine, index) => (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, String(index))))] }) }) }));
    }
    // 柱状图配置
    if (chartType === "column") {
        // 如果是多系列数据
        if (seriesField) {
            // 按系列分组数据
            const seriesMap = new Map();
            processedData.forEach((item) => {
                const seriesType = item[seriesField];
                if (!seriesMap.has(seriesType)) {
                    seriesMap.set(seriesType, []);
                }
                seriesMap.get(seriesType).push(item);
            });
            // 转换为 recharts 需要的格式（按 x 值分组）
            const xValueMap = new Map();
            processedData.forEach((item) => {
                const xValue = item[xField || "index"];
                if (!xValueMap.has(xValue)) {
                    xValueMap.set(xValue, { [xField || "index"]: xValue });
                }
                const seriesType = item[seriesField];
                xValueMap.get(xValue)[seriesType] = item[yFields[0] || "value"];
            });
            const chartData = Array.from(xValueMap.values());
            return (_jsx("div", { style: { height: chartHeight, width: chartWidth }, children: _jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(BarChart, { data: chartData, margin: { top: 0, right: 0, bottom: 0, left: 0 }, children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: () => null }), _jsx(Legend, { content: () => null }), Array.from(seriesMap.keys()).map((seriesType, index) => (_jsx(Bar, { dataKey: seriesType, fill: colors[index % colors.length], ...{ radius: [2, 2, 0, 0] }, isAnimationActive: false }, seriesType))), referenceLines?.map((refLine, index) => (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, `ref-line-${refLine.value}-${index}`)))] }) }) }));
        }
        // 单系列柱状图
        return (_jsx("div", { style: { height: chartHeight, width: chartWidth }, children: _jsx(ResponsiveContainer, { width: "100%", height: "100%", children: _jsxs(BarChart, { data: processedData, margin: { top: 0, right: 0, bottom: 0, left: 0 }, children: [_jsx(XAxis, { dataKey: xField || "index", hide: true }), _jsx(YAxis, { hide: true }), _jsx(Tooltip, { content: () => null }), _jsx(Bar, { dataKey: yFields[0] || "value", fill: primaryColor, ...{ radius: [2, 2, 0, 0] }, isAnimationActive: false }), referenceLines?.map((refLine, index) => (_jsx(ReferenceLine, { y: refLine.value, stroke: refLine.color || "#999", strokeDasharray: refLine.strokeDasharray || "5 5", label: refLine.label ? { value: refLine.label, position: "right" } : undefined }, String(index))))] }) }) }));
    }
    return null;
};
// 对齐选择器组件
const AlignSelectCell = ({ value, record, dataIndex, onAlignChange, }) => {
    const [editing, setEditing] = useState(false);
    const alignValue = value || "left";
    const alignLabel = alignValue === "left" ? "左" : alignValue === "center" ? "中" : "右";
    const options = [
        { label: "左", value: "left" },
        { label: "中", value: "center" },
        { label: "右", value: "right" },
    ];
    if (editing) {
        return (_jsx(Select, { value: alignValue, open: true, autoFocus: true, onBlur: () => setEditing(false), onChange: (newAlign) => {
                if (onAlignChange) {
                    onAlignChange(newAlign, record, dataIndex || "");
                }
                setEditing(false);
            }, size: "small", style: { width: "100%" }, options: options }));
    }
    return (_jsx("div", { onClick: () => setEditing(true), style: {
            cursor: "pointer",
            padding: "4px 8px",
            minHeight: "22px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        }, children: alignLabel }));
};
// 生成单元格组件渲染函数
const createCellRenderer = (cellType, cellConfig, dataIndex) => {
    switch (cellType) {
        case "tag": {
            return (value) => {
                if (cellConfig?.options) {
                    const option = cellConfig.options.find((opt) => opt.value === value);
                    if (option) {
                        return (_jsx(Tag, { color: option.color || cellConfig.color, children: option.label }));
                    }
                }
                return _jsx(Tag, { color: cellConfig?.color, children: value });
            };
        }
        case "switch": {
            return (value, record, index) => {
                const disabled = typeof cellConfig?.disabled === "function"
                    ? cellConfig.disabled(record)
                    : (cellConfig?.disabled ?? false);
                return (_jsx(Switch, { size: cellConfig?.size || "default", checked: value, disabled: disabled, onChange: (checked) => {
                        if (cellConfig?.onChange) {
                            cellConfig.onChange(checked, record, dataIndex || "");
                        }
                    } }));
            };
        }
        case "badge": {
            return (value) => {
                return (_jsx(Badge, { status: cellConfig?.status || "default", text: cellConfig?.text || value }));
            };
        }
        case "alignSelect": {
            return (value, record, index) => {
                return (_jsx(AlignSelectCell, { value: value, record: record, dataIndex: dataIndex, onAlignChange: cellConfig?.onAlignChange }));
            };
        }
        case "minichart": {
            return (value, record, index) => {
                // 获取图表数据
                let chartData = [];
                // 如果 value 是数组，直接使用
                if (Array.isArray(value)) {
                    chartData = value;
                    // 确保数组中有有效的数字
                    if (chartData.length > 0 && typeof chartData[0] === 'number') {
                        // 数字数组格式正确
                    }
                    else {
                        // 如果不是数字数组，尝试转换
                        chartData = chartData.filter((item) => typeof item === 'number');
                    }
                }
                // 如果配置了 dataField，从 record 中获取数据
                else if (cellConfig?.dataField) {
                    const dataField = cellConfig.dataField;
                    if (Array.isArray(dataField)) {
                        // 多系列数据：将多个字段的数据组合成二维数组
                        const seriesData = dataField.map((field) => {
                            const fieldData = record[field];
                            return Array.isArray(fieldData) ? fieldData : [];
                        });
                        // 如果所有系列都是数组，则传递二维数组给 MiniChart
                        if (seriesData.every((arr) => arr.length > 0)) {
                            chartData = seriesData;
                        }
                    }
                    else {
                        // 单系列数据
                        const fieldData = record[dataField];
                        chartData = Array.isArray(fieldData) ? fieldData : [];
                    }
                }
                // 如果 value 是对象且有数据字段
                else if (value && typeof value === "object" && !Array.isArray(value)) {
                    // 尝试从对象中提取数组数据
                    const keys = Object.keys(value);
                    const arrayKeys = keys.filter((key) => Array.isArray(value[key]));
                    if (arrayKeys.length > 0) {
                        // 如果有多个数组字段，组合成多系列
                        if (arrayKeys.length > 1) {
                            chartData = arrayKeys.map((key) => value[key]);
                        }
                        else {
                            chartData = value[arrayKeys[0]];
                        }
                    }
                }
                // 如果没有数据，返回空
                if (!chartData || chartData.length === 0) {
                    return _jsx("div", { style: { height: cellConfig?.height || 40, width: '100%', backgroundColor: '#f0f0f0' } });
                }
                // 确保数据有效：支持一维 number[] 或二维 number[][]（多系列）
                let validData = [];
                if (Array.isArray(chartData)) {
                    const first = chartData[0];
                    if (typeof first === 'number') {
                        validData = chartData.filter((item) => typeof item === 'number');
                    }
                    else if (Array.isArray(first)) {
                        validData = chartData.map((arr) => Array.isArray(arr) ? arr.filter((item) => typeof item === 'number') : []).filter((arr) => arr.length > 0);
                    }
                    else {
                        validData = chartData.filter((item) => typeof item === 'number');
                    }
                }
                const hasData = Array.isArray(validData) && (validData.length > 0 &&
                    (typeof validData[0] === 'number' || (Array.isArray(validData[0]) && validData[0].length > 0)));
                if (!hasData) {
                    return _jsx("div", { style: { height: cellConfig?.height || 40, width: '100%', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#999' }, children: "\u65E0\u6570\u636E" });
                }
                // 确保高度至少为16px
                const chartHeight = Math.max(cellConfig?.height || 40, 16);
                return (_jsx("div", { style: {
                        width: '100%',
                        height: chartHeight,
                        minHeight: chartHeight,
                        position: 'relative',
                        overflow: 'visible',
                        display: 'block',
                    }, children: _jsx(MiniChart, { data: validData, chartType: cellConfig?.chartType || "line", xField: cellConfig?.xField, height: chartHeight, width: undefined, color: cellConfig?.chartColor, smooth: cellConfig?.smooth, referenceLines: 
                        // 优先使用 cellConfig 中的 referenceLines
                        // 如果没有，尝试从 record 中读取 normalizedReferenceLines
                        cellConfig?.referenceLines ||
                            record?.normalizedReferenceLines }) }));
            };
        }
        case "progress": {
            return (value, record, index) => {
                // 获取进度百分比
                const percent = cellConfig?.percent !== undefined
                    ? cellConfig.percent
                    : (typeof value === "number" ? value : Number(value) || 0);
                // 限制在 0-100 之间
                const clampedPercent = Math.min(Math.max(percent, 0), 100);
                return (_jsx(Progress, { percent: clampedPercent, strokeColor: cellConfig?.strokeColor || "#1890ff", trailColor: "#f0f0f0", showInfo: false, size: "small", style: { width: "100%" } }));
            };
        }
        case "text":
        default: {
            return (value) => value;
        }
    }
};
// 添加ResizableTitle组件
const ResizableTitle = (props) => {
    const { onResize, width, cellComponent: CellComponent = "th", ...restProps } = props;
    const HeaderCellComponent = CellComponent;
    if (!width) {
        return _jsx(HeaderCellComponent, { ...restProps });
    }
    return (_jsx(Resizable, { width: width, height: 0, onResize: onResize, draggableOpts: { enableUserSelectHack: false }, 
        // 添加最小最大宽度限制（最小宽度设为10，允许列宽缩到很小）
        minConstraints: [10, 0], maxConstraints: [MAX_COLUMN_WIDTH, 0], handle: _jsx("span", { className: "react-resizable-handle", style: {
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: 8,
                cursor: "col-resize",
                zIndex: 1000,
                userSelect: "none",
                touchAction: "none",
            } }), children: _jsx(HeaderCellComponent, { ...restProps, style: { ...restProps.style, position: "relative", userSelect: "none" } }) }));
};
/**
 * 通用表格组件（内核）
 * 只包含表格核心功能，不包含容器
 */
function CompsTable({ tableProps, loading = false, showSelection = false, pagination, onPaginationChange, defaultPageSize = 10, showColumnSetting = true, storageKey, resizableColumns = false, saveColumnWidths = true, onColumnWidthChange, defaultActions, summary, summaryWithColumns, footer, className, style, onColumnSettingButtonRender, onSelectionChange, autoScroll = true, scrollOffset = 0, }) {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    // selectedRows 状态用于传递给 onSelectionChange 回调
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedRows, setSelectedRows] = useState([]);
    const [popoverVisible, setPopoverVisible] = useState(false);
    // 表格容器引用和滚动高度
    const containerRef = useRef(null);
    const [scrollHeight, setScrollHeight] = useState(undefined);
    // 内部分页状态（当没有传入分页时使用）
    const [internalPagination, setInternalPagination] = useState({
        current: 1,
        pageSize: defaultPageSize,
        total: 0,
    });
    // 使用外部传入的分页或内部分页
    // 如果 tableProps.pagination 明确设置为 false，则不显示分页
    const currentPagination = tableProps.pagination === false ? false : pagination || internalPagination;
    // 计算滚动高度
    useEffect(() => {
        if (!autoScroll || !containerRef.current) {
            return;
        }
        const calculateScrollHeight = () => {
            const container = containerRef.current;
            if (!container)
                return;
            // 获取容器总高度
            const containerHeight = container.clientHeight;
            if (containerHeight === 0) {
                // 容器还没有高度，设置为 undefined，等待下次计算
                setScrollHeight(undefined);
                return;
            }
            // 获取表头高度（表头是固定的，需要从滚动区域中减去）
            const thead = container.querySelector(".ant-table-thead");
            const theadHeight = thead ? thead.getBoundingClientRect().height : 0;
            // 获取分页高度（分页在表格外部，固定在底部）
            // 如果分页存在，使用实际高度；如果不存在，使用默认高度预留空间
            const pagination = container.querySelector(".ant-pagination");
            let paginationHeight = 0;
            // 如果分页被禁用，则不需要预留空间
            // 注意：这里需要重新获取 currentPagination，因为闭包可能捕获旧值
            const actualPagination = tableProps.pagination === false
                ? false
                : pagination || internalPagination;
            if (actualPagination !== false) {
                if (pagination) {
                    const paginationRect = pagination.getBoundingClientRect();
                    paginationHeight = paginationRect.height;
                    // 获取分页的 margin
                    const paginationStyle = window.getComputedStyle(pagination);
                    const marginTop = parseFloat(paginationStyle.marginTop) || 0;
                    const marginBottom = parseFloat(paginationStyle.marginBottom) || 0;
                    paginationHeight += marginTop + marginBottom;
                }
                else {
                    // 分页还没渲染，使用默认高度预留空间（通常分页高度约 64px，包括 margin）
                    paginationHeight = 64;
                }
            }
            // 计算表格 body 的滚动高度
            // scroll.y 是表格 body 的高度，不包括表头
            // 所以：容器高度 - 表头高度 - 分页高度 - 偏移量 = 表格 body 高度
            const bodyHeight = containerHeight - theadHeight - paginationHeight - scrollOffset;
            // 设置最小高度为 100px，确保表格可以显示
            // 但如果容器本身很小，也要允许表格缩小
            const finalHeight = Math.max(bodyHeight, 100);
            setScrollHeight(finalHeight);
        };
        // 初始计算（延迟执行，确保 DOM 渲染完成）
        // 多次尝试计算，因为 DOM 可能需要时间渲染
        const initialTimer1 = setTimeout(calculateScrollHeight, 50);
        const initialTimer2 = setTimeout(calculateScrollHeight, 150);
        const initialTimer3 = setTimeout(calculateScrollHeight, 300);
        // 使用 ResizeObserver 监听容器大小变化
        const resizeObserver = new ResizeObserver((entries) => {
            // 延迟执行，确保 DOM 更新完成
            // 使用 requestAnimationFrame 确保在下一帧计算
            requestAnimationFrame(() => {
                setTimeout(calculateScrollHeight, 0);
            });
        });
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }
        // 监听窗口大小变化
        window.addEventListener("resize", calculateScrollHeight);
        return () => {
            clearTimeout(initialTimer1);
            clearTimeout(initialTimer2);
            clearTimeout(initialTimer3);
            resizeObserver.disconnect();
            window.removeEventListener("resize", calculateScrollHeight);
        };
    }, [
        autoScroll,
        scrollOffset,
        loading,
        pagination,
        tableProps.pagination,
        internalPagination,
        // 分页显隐、条数变化时重算 body 高度，避免分页被挤出可视区
        currentPagination === false
            ? "off"
            : `${currentPagination?.current ?? ""}-${currentPagination?.pageSize ?? ""}-${currentPagination?.total ?? ""}`,
    ]);
    // 添加列宽状态
    const [columnWidths, setColumnWidths] = useState({});
    // 列可见性状态
    const [columnVisibility, setColumnVisibility] = useState({});
    // 获取表格所有列
    const allColumns = useMemo(() => tableProps.columns || [], [tableProps.columns]);
    const rowKeyProp = tableProps.rowKey;
    const resolveRowKey = useCallback((record, index) => {
        if (typeof rowKeyProp === "function") {
            return rowKeyProp(record, index);
        }
        if (typeof rowKeyProp === "string") {
            return record[rowKeyProp];
        }
        return record?.key ?? index;
    }, [rowKeyProp]);
    // 存储键
    const actualStorageKey = useMemo(() => storageKey || "table_columns_default", [storageKey]);
    // 为每列生成唯一标识
    const getColumnKey = useCallback((column) => {
        return (column.dataIndex || column.key || column.title)?.toString() || "";
    }, []);
    // 处理单元格组件的辅助函数
    const processCellComponent = useCallback((col) => {
        // 扩展列类型，支持单元格组件
        const extendedCol = col;
        // 如果指定了 cellType 且没有自定义 render，自动生成 render 函数
        if (extendedCol.cellType && !col.render) {
            let dataIndex;
            if (typeof col.dataIndex === "string") {
                dataIndex = col.dataIndex;
            }
            else if (Array.isArray(col.dataIndex)) {
                // 将数组中的每个元素转换为字符串
                dataIndex = col.dataIndex.map(String);
            }
            else if (col.dataIndex !== undefined && col.dataIndex !== null) {
                dataIndex = String(col.dataIndex);
            }
            else {
                dataIndex = undefined;
            }
            const finalRender = createCellRenderer(extendedCol.cellType, extendedCol.cellConfig, dataIndex);
            return {
                ...col,
                render: finalRender,
            };
        }
        return col;
    }, []);
    // 过滤显示的列 - 只定义一次，并处理单元格组件
    const visibleColumns = useMemo(() => allColumns
        .filter((column) => {
        const key = getColumnKey(column);
        return !key || columnVisibility[key] !== false;
    })
        .map(processCellComponent), [allColumns, columnVisibility, getColumnKey, processCellComponent]);
    // 初始化列宽
    useEffect(() => {
        if (resizableColumns && saveColumnWidths) {
            try {
                const savedWidths = localStorage.getItem(`${actualStorageKey}_widths`);
                if (savedWidths) {
                    setColumnWidths(JSON.parse(savedWidths));
                }
            }
            catch (error) {
                console.error("加载列宽配置失败:", error);
            }
        }
    }, [resizableColumns, saveColumnWidths, actualStorageKey]);
    // 保存列宽到本地存储
    useEffect(() => {
        if (resizableColumns &&
            saveColumnWidths &&
            Object.keys(columnWidths).length > 0) {
            try {
                localStorage.setItem(`${actualStorageKey}_widths`, JSON.stringify(columnWidths));
            }
            catch (error) {
                console.error("保存列宽配置失败:", error);
            }
        }
    }, [columnWidths, resizableColumns, saveColumnWidths, actualStorageKey]);
    // 处理列宽调整
    const handleResize = useCallback((index) => (e, { size }) => {
        const column = visibleColumns[index];
        const key = column ? getColumnKey(column) : null;
        if (!key) {
            return;
        }
        let newWidth = size.width;
        // 最小宽度设为10，允许列宽缩到很小
        if (newWidth < 10)
            newWidth = 10;
        if (newWidth > MAX_COLUMN_WIDTH)
            newWidth = MAX_COLUMN_WIDTH;
        setColumnWidths((prev) => {
            if (prev[key] === newWidth) {
                return prev;
            }
            const newWidths = { ...prev, [key]: newWidth };
            // 调用外部回调通知列宽变化
            if (onColumnWidthChange) {
                onColumnWidthChange(key, newWidth);
            }
            return newWidths;
        });
    }, [visibleColumns, getColumnKey, onColumnWidthChange]);
    const userComponents = tableProps.components;
    const userRowSelection = tableProps.rowSelection;
    // 处理表格组件
    const mergedComponents = useMemo(() => {
        if (!resizableColumns) {
            return userComponents;
        }
        const headerComponents = userComponents?.header;
        const headerCellComponent = headerComponents?.cell && typeof headerComponents.cell !== "undefined"
            ? headerComponents.cell
            : "th";
        const ResizableHeaderCell = (props) => (_jsx(ResizableTitle, { ...props, cellComponent: headerCellComponent }));
        return {
            ...userComponents,
            header: {
                ...(headerComponents || {}),
                cell: ResizableHeaderCell,
            },
        };
    }, [resizableColumns, userComponents]);
    // 添加列宽重置函数
    const resetColumnWidths = useCallback(() => {
        setColumnWidths({});
        try {
            localStorage.removeItem(`${actualStorageKey}_widths`);
        }
        catch (error) {
            console.error("移除列宽配置失败:", error);
        }
        setPopoverVisible(false);
    }, [actualStorageKey]);
    // 设置列宽和处理函数
    // 注意：visibleColumns 已经处理了 cellType，所以这里只需要处理列宽
    const resizedColumns = useMemo(() => visibleColumns.map((col, index) => {
        const key = getColumnKey(col);
        const savedWidth = key && columnWidths[key] ? columnWidths[key] : undefined;
        // 确保所有列都有 width，如果没有则使用默认值或最小宽度（降低到10）
        const width = savedWidth || col.width || 10;
        const originalOnHeaderCell = col.onHeaderCell;
        const resizeHandler = handleResize(index);
        return {
            ...col,
            width,
            onHeaderCell: (column) => {
                const originalProps = typeof originalOnHeaderCell === "function"
                    ? originalOnHeaderCell(column) || {}
                    : {};
                const originalResize = originalProps.onResize;
                return {
                    ...originalProps,
                    width,
                    onResize: (event, data) => {
                        if (typeof originalResize === "function") {
                            originalResize(event, data);
                        }
                        resizeHandler(event, data);
                    },
                };
            },
        };
    }), [visibleColumns, columnWidths, getColumnKey, handleResize]);
    const visibilityInitializedRef = useRef(null);
    const getDefaultVisibility = useCallback(() => {
        const defaultVisibility = {};
        allColumns.forEach((column) => {
            const key = getColumnKey(column);
            if (key) {
                defaultVisibility[key] = true;
            }
        });
        return defaultVisibility;
    }, [allColumns, getColumnKey]);
    // 初始化列可见性并在列变更时补全/清理配置
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        // 初次或 storageKey 变更时加载本地配置
        if (visibilityInitializedRef.current !== actualStorageKey) {
            visibilityInitializedRef.current = actualStorageKey;
            try {
                const savedConfig = localStorage.getItem(actualStorageKey);
                if (savedConfig) {
                    const parsedConfig = JSON.parse(savedConfig);
                    setColumnVisibility((prev) => {
                        const mergedConfig = {
                            ...getDefaultVisibility(),
                            ...parsedConfig,
                        };
                        const isSame = Object.keys(mergedConfig).length === Object.keys(prev).length &&
                            Object.keys(mergedConfig).every((key) => prev[key] === mergedConfig[key]);
                        return isSame ? prev : mergedConfig;
                    });
                }
                else {
                    setColumnVisibility((prev) => {
                        const defaultVisibility = getDefaultVisibility();
                        const isSame = Object.keys(defaultVisibility).length ===
                            Object.keys(prev).length &&
                            Object.keys(defaultVisibility).every((key) => prev[key] === defaultVisibility[key]);
                        return isSame ? prev : defaultVisibility;
                    });
                }
            }
            catch (error) {
                console.error("加载列配置失败:", error);
                setColumnVisibility((prev) => {
                    const fallbackVisibility = getDefaultVisibility();
                    const isSame = Object.keys(fallbackVisibility).length ===
                        Object.keys(prev).length &&
                        Object.keys(fallbackVisibility).every((key) => prev[key] === fallbackVisibility[key]);
                    return isSame ? prev : fallbackVisibility;
                });
            }
            return;
        }
        // 当列集合变化时补充/清理可见性配置
        setColumnVisibility((prev) => {
            const nextVisibility = { ...prev };
            let changed = false;
            // 补充新列
            allColumns.forEach((column) => {
                const key = getColumnKey(column);
                if (!key) {
                    return;
                }
                if (!(key in nextVisibility)) {
                    nextVisibility[key] = true;
                    changed = true;
                }
            });
            // 移除已不存在的列
            Object.keys(nextVisibility).forEach((key) => {
                const stillExists = allColumns.some((column) => getColumnKey(column) === key);
                if (!stillExists) {
                    delete nextVisibility[key];
                    changed = true;
                }
            });
            return changed ? nextVisibility : prev;
        });
    }, [actualStorageKey, allColumns, getColumnKey, getDefaultVisibility]);
    // 保存列可见性到本地存储
    useEffect(() => {
        // 仅当columnVisibility不为空对象时保存
        if (Object.keys(columnVisibility).length > 0) {
            try {
                localStorage.setItem(actualStorageKey, JSON.stringify(columnVisibility));
            }
            catch (error) {
                console.error("保存列配置失败:", error);
            }
        }
    }, [columnVisibility, actualStorageKey]);
    // 处理列可见性变化
    const handleColumnVisibilityChange = useCallback((checkedValues) => {
        const newVisibility = {};
        // 先将所有列设为不可见
        allColumns.forEach((column) => {
            const key = getColumnKey(column);
            if (key) {
                newVisibility[key] = false;
            }
        });
        // 再将选中的列设为可见
        checkedValues.forEach((value) => {
            newVisibility[value] = true;
        });
        setColumnVisibility(newVisibility);
    }, [allColumns, getColumnKey]);
    // 重置列可见性，恢复所有列显示
    const resetColumnVisibility = useCallback(() => {
        const resetVisibility = {};
        allColumns.forEach((column) => {
            const key = getColumnKey(column);
            if (key) {
                resetVisibility[key] = true;
            }
        });
        setColumnVisibility(resetVisibility);
        setPopoverVisible(false);
    }, [allColumns, getColumnKey]);
    // 处理表格选择变化
    const handleSelectionChange = useCallback((newSelectedRowKeys, newSelectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
        // 通知外部组件选中行变化
        if (onSelectionChange) {
            onSelectionChange(newSelectedRows);
        }
    }, [onSelectionChange]);
    // 处理表格变化
    const handleTableChange = (newPagination, filters, sorter, extra) => {
        // 如果有外部传入的回调，则调用
        if (onPaginationChange) {
            onPaginationChange(newPagination);
        }
        else {
            // 否则使用内部状态
            setInternalPagination(newPagination);
        }
        // 确保传递所有参数
        if (tableProps.onChange) {
            tableProps.onChange(newPagination, filters, sorter, extra);
        }
    };
    // 列设置弹窗内容
    const columnSettingContent = useMemo(() => {
        if (!showColumnSetting) {
            return null;
        }
        return (_jsxs("div", { style: { maxWidth: 300 }, children: [_jsxs("div", { style: {
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 8,
                    }, children: [_jsx("span", { style: { fontWeight: "bold" }, children: "\u663E\u793A\u7684\u5217" }), _jsxs(Space, { children: [_jsx(Button, { type: "link", size: "small", onClick: resetColumnVisibility, title: "\u91CD\u7F6E\u5217\u663E\u793A", style: { fontSize: "12px" }, children: "\u91CD\u7F6E\u663E\u793A" }), _jsx(Button, { type: "link", size: "small", onClick: resetColumnWidths, title: "\u91CD\u7F6E\u5217\u5BBD", danger: true, style: { fontSize: "12px" }, children: "\u91CD\u7F6E\u5BBD\u5EA6" })] })] }), _jsx(Divider, { style: { margin: "8px 0" } }), _jsx(Checkbox.Group, { style: { display: "flex", flexDirection: "column", gap: "8px" }, value: Object.entries(columnVisibility)
                        .filter(([_, visible]) => visible)
                        .map(([key]) => key), onChange: handleColumnVisibilityChange, children: allColumns.map((column) => {
                        const key = getColumnKey(column);
                        if (!key)
                            return null;
                        const title = typeof column.title === "string" ? column.title : `列 ${key}`;
                        return (_jsx(Checkbox, { value: key, children: title }, key));
                    }) })] }));
    }, [
        allColumns,
        columnVisibility,
        getColumnKey,
        handleColumnVisibilityChange,
        resetColumnVisibility,
        resetColumnWidths,
        showColumnSetting,
    ]);
    // 列设置按钮
    const columnSettingButton = useMemo(() => {
        if (!showColumnSetting || !columnSettingContent) {
            return null;
        }
        return (_jsx(Tooltip, { title: "\u5217\u8BBE\u7F6E", children: _jsx(Popover, { content: columnSettingContent, title: null, trigger: "click", open: popoverVisible, onOpenChange: setPopoverVisible, placement: "rightTop", overlayStyle: { minWidth: "60px" }, children: _jsx(Button, { icon: React.createElement(SettingOutlined), type: "text", size: "small", style: { color: "#666" }, onClick: (e) => {
                        e.stopPropagation();
                        setPopoverVisible(!popoverVisible);
                    } }) }) }));
    }, [columnSettingContent, popoverVisible, showColumnSetting]);
    const columnSettingButtonSignature = useMemo(() => {
        const columnKeys = allColumns
            .map((column) => getColumnKey(column))
            .join("|");
        const visibleKeys = Object.entries(columnVisibility)
            .filter(([, visible]) => visible)
            .map(([key]) => key)
            .sort()
            .join("|");
        return `${columnKeys}__${visibleKeys}__${popoverVisible ? "open" : "closed"}`;
    }, [allColumns, columnVisibility, getColumnKey, popoverVisible]);
    const lastRenderedSignatureRef = useRef(null);
    useEffect(() => {
        lastRenderedSignatureRef.current = null;
    }, [onColumnSettingButtonRender]);
    // 将列设置按钮传递给父组件
    useEffect(() => {
        if (!onColumnSettingButtonRender) {
            return;
        }
        if (columnSettingButtonSignature === lastRenderedSignatureRef.current) {
            return;
        }
        lastRenderedSignatureRef.current = columnSettingButtonSignature;
        onColumnSettingButtonRender(columnSettingButton);
    }, [
        columnSettingButton,
        columnSettingButtonSignature,
        onColumnSettingButtonRender,
    ]);
    // 创建缺省操作列
    const createDefaultActionColumn = () => {
        if (!defaultActions)
            return null;
        const { onEdit, onDelete, onTest, editIcon, deleteIcon, testIcon, width = 120, spacing = 16, } = defaultActions;
        // 如果没有提供任何操作回调，则不创建操作列
        if (!onEdit && !onDelete && !onTest)
            return null;
        return {
            title: "操作",
            key: "defaultActions",
            width,
            fixed: "right",
            render: (_, record) => {
                const actions = [];
                if (onEdit) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: editIcon || React.createElement(EditOutlined), onClick: () => onEdit(record), style: { padding: 0, fontSize: "12px" } }, "edit"));
                }
                if (onTest) {
                    actions.push(_jsx(Button, { type: "link", size: "small", icon: testIcon || React.createElement(TestIcon), onClick: () => onTest(record), style: { padding: 0, fontSize: "12px" } }, "test"));
                }
                if (onDelete) {
                    actions.push(_jsx(Button, { type: "link", size: "small", danger: true, icon: deleteIcon || React.createElement(DeleteOutlined), onClick: () => onDelete(record), style: { padding: 0, fontSize: "12px" } }, "delete"));
                }
                return _jsx(Space, { size: spacing, children: actions });
            },
        };
    };
    // 获取缺省操作列
    const defaultActionColumn = useMemo(() => createDefaultActionColumn(), [defaultActions]);
    const mergedRowSelection = useMemo(() => {
        if (!showSelection) {
            return userRowSelection;
        }
        const userOnChange = userRowSelection?.onChange;
        return {
            ...(userRowSelection || {}),
            selectedRowKeys,
            onChange: (keys, rows, info) => {
                handleSelectionChange(keys, rows);
                if (typeof userOnChange === "function") {
                    userOnChange(keys, rows, info);
                }
            },
        };
    }, [showSelection, userRowSelection, selectedRowKeys, handleSelectionChange]);
    useEffect(() => {
        if (!showSelection) {
            return;
        }
        const externalSelectedKeys = userRowSelection?.selectedRowKeys;
        if (!Array.isArray(externalSelectedKeys)) {
            return;
        }
        setSelectedRowKeys((prevKeys) => {
            if (Array.isArray(prevKeys) &&
                prevKeys.length === externalSelectedKeys.length &&
                prevKeys.every((key, index) => key === externalSelectedKeys[index])) {
                return prevKeys;
            }
            return [...externalSelectedKeys];
        });
        if (Array.isArray(tableProps.dataSource)) {
            const selectedKeySet = new Set(externalSelectedKeys);
            const mappedSelectedRows = tableProps.dataSource.filter((record, index) => selectedKeySet.has(resolveRowKey(record, index)));
            setSelectedRows(mappedSelectedRows);
        }
    }, [
        showSelection,
        userRowSelection?.selectedRowKeys,
        tableProps.dataSource,
        resolveRowKey,
    ]);
    // 合并列配置
    const finalColumns = useMemo(() => {
        const baseColumns = resizableColumns ? resizedColumns : visibleColumns;
        const hasActionColumn = baseColumns.some((col) => col.key === "action" ||
            col.key === "defaultActions" ||
            (typeof col.title === "string" && col.title.includes("操作")));
        if (defaultActionColumn && !hasActionColumn) {
            return [...baseColumns, defaultActionColumn];
        }
        return baseColumns;
    }, [resizableColumns, resizedColumns, visibleColumns, defaultActionColumn]);
    // 合并滚动配置
    const mergedScroll = useMemo(() => {
        const userScroll = tableProps.scroll;
        if (!autoScroll) {
            return userScroll;
        }
        // 如果用户已经设置了 scroll.y，则使用用户的配置
        if (userScroll?.y !== undefined) {
            return userScroll;
        }
        // 自动计算滚动高度
        // 即使 scrollHeight 为 undefined 或 0，也要设置一个合理的默认值
        // 这样可以确保表格始终有滚动能力
        if (scrollHeight !== undefined && scrollHeight > 0) {
            return {
                ...userScroll,
                y: scrollHeight,
                x: userScroll?.x || "max-content",
            };
        }
        // 如果 scrollHeight 还没计算出来，返回一个临时的滚动配置
        // 使用一个较大的默认值，等待计算完成后再更新
        return {
            ...userScroll,
            y: 400,
            x: userScroll?.x || "max-content",
        };
    }, [autoScroll, scrollHeight, tableProps.scroll]);
    // 构建最终的表格属性
    // 确定 summary 值：如果明确传入 undefined，则完全禁用；否则使用传入的 summary 或 tableProps.summary
    // 优先使用 summaryWithColumns（如果提供），它会接收最终列配置
    const finalSummary = useMemo(() => {
        if (summaryWithColumns) {
            // 如果提供了 summaryWithColumns，使用它并传入 finalColumns
            // 注意：这里返回一个函数，该函数会在每次渲染时使用最新的 finalColumns
            return (pageData) => {
                return summaryWithColumns(pageData, finalColumns);
            };
        }
        if (summary === undefined && tableProps.summary === undefined) {
            return undefined;
        }
        return summary !== undefined ? summary : tableProps.summary;
    }, [summary, summaryWithColumns, tableProps.summary, finalColumns]);
    // 构建基础表格属性，排除 summary（如果为 undefined）
    const mergedTableClassName = [tableProps.className, "comps-table-root"]
        .filter(Boolean)
        .join(" ");
    const baseTableProps = {
        ...tableProps,
        className: mergedTableClassName,
        rowSelection: mergedRowSelection,
        loading,
        pagination: currentPagination,
        onChange: handleTableChange,
        // 使用合并后的列配置
        columns: finalColumns,
        components: mergedComponents,
        footer: footer !== undefined ? footer : tableProps.footer,
        scroll: mergedScroll,
    };
    // 只有当 finalSummary 不是 undefined 时才添加 summary 属性
    const finalTableProps = {
        ...baseTableProps,
        ...(finalSummary !== undefined ? { summary: finalSummary } : {}),
    };
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
          /* 可调整列宽样式 */
          .react-resizable {
            position: relative;
            background-clip: padding-box;
          }

          .react-resizable-handle {
            position: absolute;
            right: 0;
            top: 0;
            bottom: 0;
            width: 8px;
            height: 100%;
            cursor: col-resize;
            z-index: 1000 !important;
            background: transparent;
            pointer-events: auto !important;
            touch-action: none;
          }

          /* 增强悬停效果 */
          .react-resizable-handle:hover {
            background-color: rgba(24, 144, 255, 0.15);
          }

          /* 拖拽时显示的辅助线 - 更加明显 */
          .react-resizable-handle:hover::after,
          .react-resizable-handle-active::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            right: 3px;
            width: 2px;
            background-color: #1890ff;
            opacity: 1;
          }

          /* 列头单元格样式调整 */
          th.ant-table-cell {
            position: relative !important;
          }

          /* 确保固定列也可以调整宽度 */
          th.ant-table-cell-fix-left,
          th.ant-table-cell-fix-right {
            position: relative !important;
          }

          /* 确保固定列中的拖动手柄可以正常工作 */
          th.ant-table-cell-fix-left .react-resizable-handle,
          th.ant-table-cell-fix-right .react-resizable-handle {
            z-index: 1001 !important;
            pointer-events: auto !important;
            touch-action: none;
          }

          /* 确保第一列（最左侧固定列）的拖动手柄可见 */
          th.ant-table-cell-fix-left:first-child .react-resizable-handle {
            display: block !important;
            visibility: visible !important;
            opacity: 0.3 !important;
          }

          th.ant-table-cell-fix-left:first-child:hover .react-resizable-handle {
            opacity: 1 !important;
          }

          /* 确保拖动手柄在所有情况下都可见且可交互 */
          .ant-table-thead > tr > th .react-resizable-handle {
            display: block !important;
            visibility: visible !important;
            opacity: 0.3;
            transition: opacity 0.2s;
            pointer-events: auto !important;
            touch-action: none;
          }

          .ant-table-thead > tr > th:hover .react-resizable-handle {
            opacity: 1;
          }

          /* 确保拖拽时手柄可见 */
          .react-resizable-handle-active {
            opacity: 1 !important;
            background-color: rgba(24, 144, 255, 0.2) !important;
            z-index: 1001 !important;
          }

          /* 防止表格内容选择干扰拖拽 */
          .ant-table-thead > tr > th {
            user-select: none;
          }

          /* 确保 Resizable 容器可以接收事件 */
          .react-resizable > * {
            pointer-events: auto;
          }

          /* CompsTable：表格区可滚动，分页条固定在组件可视区域底部 */
          .comps-table-root.ant-table-wrapper {
            display: flex !important;
            flex-direction: column;
            height: 100%;
            min-height: 0;
            flex: 1;
          }
          .comps-table-root .ant-spin-nested-loading {
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
          .comps-table-root .ant-spin-container {
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
          .comps-table-root .ant-table {
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
          .comps-table-root .ant-table-title {
            flex-shrink: 0;
          }
          .comps-table-root .ant-table-container {
            flex: 1;
            min-height: 0;
          }
          .comps-table-root .ant-table-footer {
            flex-shrink: 0;
          }
          .comps-table-root .ant-table-pagination {
            flex-shrink: 0;
            margin-top: auto;
            position: sticky;
            bottom: 0;
            z-index: 10;
            background: var(--ant-color-bg-container, #fff);
            padding-top: 8px;
            box-shadow: 0 -4px 8px -6px rgba(0, 0, 0, 0.12);
          }
        ` }), _jsx("div", { ref: containerRef, className: className, style: {
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    width: "100%",
                    // 移除 height: "100%"，让容器能够跟随 flex 布局缩小
                    ...style,
                }, children: _jsx(Table, { ...finalTableProps }) })] }));
}
export default CompsTable;
